import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { X, Save, Cpu, Heart, Loader2, GitBranch } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import axios from '../../../../../services/http.service';
import GrowPageContext from '../../contexts/GrowPageContext';
import DialogTreeViewForm from './DialogTreeViewForm';

function DefaultTasksDialog({
  isOpened,
  commonHardSkillTree,
  commonSoftSkillTree,
  setCommonHardSkillTree,
  setCommonSoftSkillTree,
  saved,
  setSaved,
  activeTab,
}) {
  const { setIsDialogOpened, department } = useContext(GrowPageContext);

  const [alertMessage, setAlertMessage] = useState('');
  const [shallowHardSkillTree, setShallowHardSkillTree] = useState([]);
  const [shallowSoftSkillTree, setShallowSoftSkillTree] = useState([]);
  const [defaultHardSkillTree, setDefaultHardSkillTree] = useState([]);
  const [defaultSoftSkillTree, setDefaultSoftSkillTree] = useState([]);
  const [replacedTasks, setReplacedTasks] = useState([]);
  const [replacedNodes, setReplacedNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const getPath = (node, data) => {
    const path = [node.name.replace(/ /g, '-')];

    const recursion = (id) => {
      const parentIndex = data.findIndex((el) => el.children.includes(id));

      if (parentIndex > -1) {
        path.unshift(data[parentIndex].name.replace(/ /g, '-'));
        recursion(data[parentIndex].itemId);
      }
    };

    recursion(node.itemId);

    return path.join('/');
  };

  const removeReferencesAndAddPath = (data) =>
    data.map((el) => ({
      ...el,
      children: [...el.children],
      details: el.details.map((detail) => ({ ...detail })),
      path: getPath(el, data),
    }));

  useEffect(() => {
    setShallowHardSkillTree(removeReferencesAndAddPath(commonHardSkillTree));
    setShallowSoftSkillTree(removeReferencesAndAddPath(commonSoftSkillTree));
  }, [commonHardSkillTree, commonSoftSkillTree, saved]);

  useEffect(() => {
    axios({
      method: 'GET',
      url: `head/grow/${department}/default`,
    })
      .then((res) => {
        setDefaultHardSkillTree(res.hard);
        setDefaultSoftSkillTree(res.soft);
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false));
  }, [commonHardSkillTree, commonSoftSkillTree, saved, department]);

  const handleClose = () => setIsDialogOpened(false);

  const validateField = (field, fieldName, detailName, itemId) => {
    const message = detailName
      ? `Invalid "${fieldName}" in skill "${detailName}"`
      : `Invalid "${fieldName}" in skill "${itemId}"`;
    try {
      if (!field.trim()) {
        return message;
      }
      return null;
    } catch (error) {
      return message;
    }
  };

  const validateFields = (arr) => {
    const validationErrors = [];

    arr.forEach((item) => {
      const { name, details } = item;

      const nameError = validateField(name, 'name', null, item.itemId);
      if (nameError) validationErrors.push(nameError);

      details.forEach((detail) => {
        const scError = validateField(
          detail.successCriteria,
          'successCriteria',
          detail.name,
          item.itemId,
        );
        if (scError) validationErrors.push(scError);

        const mnError = validateField(
          detail.materialName,
          'materialName',
          detail.name,
          item.itemId,
        );
        if (mnError) validationErrors.push(mnError);

        const mtError = validateField(
          detail.materialType,
          'materialType',
          detail.name,
          item.itemId,
        );
        if (mtError) validationErrors.push(mtError);

        const linkError = validateField(detail.link, 'link', detail.name, item.itemId);
        if (linkError) validationErrors.push(linkError);
      });
    });

    return validationErrors;
  };

  const prepareData = (array) =>
    array.map(({ status, path, ...node }) => ({
      ...node,
      name: node.name.trim(),
      details: node.details.map(({ status: detailStatus, path: detailPath, ...detail }) => ({
        ...detail,
        name: detail.name.trim(),
        successCriteria: detail.successCriteria.trim(),
        materialName: detail.materialName.trim(),
        materialType: detail.materialType.trim().toLowerCase(),
        link: detail.link.trim(),
        contentType: !detail.contentType ? null : detail.contentType.trim().toLowerCase(),
      })),
    }));

  const handleSave = async (treeData, type) => {
    const validationErrors = validateFields(treeData);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setAlertMessage('All fields are required!');
      return false;
    }

    setIsSaving(true);
    const preparedData = prepareData(treeData);

    try {
      const response = await axios({
        method: 'POST',
        url: 'head/grow',
        data: {
          treeData: preparedData,
          department,
          skillType: type,
        },
      });

      if (type === 'hard') {
        setCommonHardSkillTree(response);
      } else {
        setCommonSoftSkillTree(response);
      }
      setSaved(true);
      handleClose();
    } catch (error) {
      setErrors([error.message]);
    } finally {
      setIsSaving(false);
    }

    return true;
  };

  if (!isOpened) return null;

  const currentTreeData = activeTab === 0 ? shallowHardSkillTree : shallowSoftSkillTree;
  const currentDefaultTreeData = activeTab === 0 ? defaultHardSkillTree : defaultSoftSkillTree;
  const setCurrentTreeData = activeTab === 0 ? setShallowHardSkillTree : setShallowSoftSkillTree;
  const setCurrentDefaultTreeData =
    activeTab === 0 ? setDefaultHardSkillTree : setDefaultSoftSkillTree;
  const skillType = activeTab === 0 ? 'hard' : 'soft';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b bg-card"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <GitBranch className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Import Default Tasks</h1>
              <p className="text-sm text-muted-foreground">Import tasks from GitLab templates</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Tab indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted">
            {activeTab === 0 ? (
              <>
                <Cpu className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Hard Skills</span>
              </>
            ) : (
              <>
                <Heart className="h-4 w-4 text-pink-500" />
                <span className="text-sm font-medium">Soft Skills</span>
              </>
            )}
          </div>

          <Button onClick={() => handleSave(currentTreeData, skillType)} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">Loading templates...</p>
            </div>
          </div>
        ) : (
          <DialogTreeViewForm
            treeData={currentTreeData}
            setTreeData={setCurrentTreeData}
            defaultTreeData={currentDefaultTreeData}
            setDefaultTreeData={setCurrentDefaultTreeData}
            replacedTasks={replacedTasks}
            setReplacedTasks={setReplacedTasks}
            replacedNodes={replacedNodes}
            setReplacedNodes={setReplacedNodes}
          />
        )}
      </div>

      {/* Error Alert */}
      {alertMessage && (
        <div className="fixed bottom-4 right-4 max-w-md">
          <div
            className="p-4 rounded-lg bg-destructive text-destructive-foreground shadow-lg"
            style={{ border: '1px solid hsl(var(--destructive))' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{alertMessage}</p>
                {errors.length > 0 && (
                  <ul className="mt-2 text-sm space-y-1 max-h-32 overflow-y-auto">
                    {errors.slice(0, 5).map((error, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index}>• {error}</li>
                    ))}
                    {errors.length > 5 && (
                      <li className="text-destructive-foreground/70">
                        ...and {errors.length - 5} more errors
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setAlertMessage('');
                  setErrors([]);
                }}
                className="text-destructive-foreground/80 hover:text-destructive-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const skillTree = {
  itemId: PropTypes.string,
  name: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      successCriteria: PropTypes.string,
      materialName: PropTypes.string,
      materialType: PropTypes.string,
      link: PropTypes.string,
      level: PropTypes.string,
    }),
  ),
  children: PropTypes.arrayOf(PropTypes.string).isRequired,
};

DefaultTasksDialog.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  commonHardSkillTree: PropTypes.arrayOf(PropTypes.shape(skillTree)).isRequired,
  commonSoftSkillTree: PropTypes.arrayOf(PropTypes.shape(skillTree)).isRequired,
  setCommonHardSkillTree: PropTypes.func.isRequired,
  setCommonSoftSkillTree: PropTypes.func.isRequired,
  saved: PropTypes.bool.isRequired,
  setSaved: PropTypes.func.isRequired,
  activeTab: PropTypes.number.isRequired,
};

export default DefaultTasksDialog;
